import { memo } from 'react'
import {
  AlarmClock,
  Briefcase,
  CalendarClock,
  CalendarX2,
  CheckCheck,
  CheckCircle2,
  CircleDashed,
  Hourglass,
  MapPinCheck,
  Navigation,
  PlayCircle,
  TimerOff,
  UserCheck,
  WifiOff,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { cn } from '@/lib/utils'

import type { JobWorker, WorkerResponse, WorkerStatus } from '../types'
import type { JobDisplayKind } from '../utils/job-status'
import { WORKER_STATUS_LABEL } from '../utils/job-status'

interface StatusVisual {
  icon: LucideIcon
  /** Chip / badge classes (text + background + ring). */
  chip: string
  /** Solid colour used for map markers and timeline blocks. */
  solid: string
  hex: string
}

export const WORKER_STATUS_VISUAL: Record<WorkerStatus, StatusVisual> = {
  available: { icon: UserCheck, chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', solid: 'bg-emerald-600', hex: '#059669' },
  on_job: { icon: Briefcase, chip: 'bg-blue-50 text-blue-700 ring-blue-200', solid: 'bg-blue-600', hex: '#2563eb' },
  late: { icon: AlarmClock, chip: 'bg-red-50 text-red-700 ring-red-200', solid: 'bg-red-600', hex: '#dc2626' },
  offline: { icon: WifiOff, chip: 'bg-slate-100 text-slate-600 ring-slate-200', solid: 'bg-slate-500', hex: '#64748b' },
}

export const JOB_STATUS_VISUAL: Record<JobDisplayKind, StatusVisual> = {
  unassigned: { icon: CircleDashed, chip: 'bg-amber-50 text-amber-800 ring-amber-200', solid: 'bg-amber-500', hex: '#f59e0b' },
  overdue: { icon: CalendarX2, chip: 'bg-red-50 text-red-700 ring-red-200', solid: 'bg-red-600', hex: '#dc2626' },
  scheduled: { icon: CalendarClock, chip: 'bg-slate-100 text-slate-700 ring-slate-200', solid: 'bg-slate-500', hex: '#64748b' },
  en_route: { icon: Navigation, chip: 'bg-indigo-50 text-indigo-700 ring-indigo-200', solid: 'bg-indigo-500', hex: '#6366f1' },
  in_progress: { icon: PlayCircle, chip: 'bg-blue-50 text-blue-700 ring-blue-200', solid: 'bg-blue-600', hex: '#2563eb' },
  completed: { icon: CheckCircle2, chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', solid: 'bg-emerald-600', hex: '#059669' },
  late: { icon: AlarmClock, chip: 'bg-red-50 text-red-700 ring-red-200', solid: 'bg-red-600', hex: '#dc2626' },
}

/** Pin colour category for a worker on the expanded job (green / amber / red / grey). */
export type WorkerPinTone = 'accepted' | 'pending' | 'alert' | 'inactive'

export const WORKER_PIN_TONE: Record<WorkerPinTone, { hex: string; solid: string; ring: string }> = {
  accepted: { hex: '#059669', solid: 'bg-emerald-600', ring: 'ring-emerald-300' },
  pending: { hex: '#d97706', solid: 'bg-amber-500', ring: 'ring-amber-300' },
  alert: { hex: '#dc2626', solid: 'bg-red-600', ring: 'ring-red-300' },
  inactive: { hex: '#64748b', solid: 'bg-slate-500', ring: 'ring-slate-300' },
}

export interface WorkerResponseVisual extends StatusVisual {
  label: string
  tone: WorkerPinTone
}

/**
 * Chip for a worker on a job: the response wins while not accepted, then the progress status.
 * Always icon + text + colour.
 */
export function getWorkerResponseVisual(jobWorker: Pick<JobWorker, 'status'>, response: WorkerResponse): WorkerResponseVisual {
  if (response === 'declined') return { label: 'Declined', icon: XCircle, chip: 'bg-red-50 text-red-700 ring-red-200', solid: 'bg-red-600', hex: '#dc2626', tone: 'alert' }
  if (response === 'expired') return { label: 'Expired', icon: TimerOff, chip: 'bg-slate-100 text-slate-600 ring-slate-200', solid: 'bg-slate-500', hex: '#64748b', tone: 'inactive' }
  if (response === 'pending') return { label: 'Pending', icon: Hourglass, chip: 'bg-amber-50 text-amber-800 ring-amber-200', solid: 'bg-amber-500', hex: '#d97706', tone: 'pending' }
  switch (jobWorker.status) {
    case 'completed':
      return { label: 'Completed', icon: CheckCheck, chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', solid: 'bg-emerald-600', hex: '#059669', tone: 'accepted' }
    case 'started':
      return { label: 'Started', icon: PlayCircle, chip: 'bg-blue-50 text-blue-700 ring-blue-200', solid: 'bg-blue-600', hex: '#2563eb', tone: 'accepted' }
    case 'on_the_way':
      return { label: 'On the way', icon: Navigation, chip: 'bg-indigo-50 text-indigo-700 ring-indigo-200', solid: 'bg-indigo-500', hex: '#6366f1', tone: 'accepted' }
    case 'checked_in':
      return { label: 'Checked in', icon: MapPinCheck, chip: 'bg-teal-50 text-teal-700 ring-teal-200', solid: 'bg-teal-600', hex: '#0d9488', tone: 'accepted' }
    default:
      return { label: 'Accepted', icon: CheckCircle2, chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', solid: 'bg-emerald-600', hex: '#059669', tone: 'accepted' }
  }
}

export const WorkerResponseChip = memo(function WorkerResponseChip({
  jobWorker,
  response,
  className,
}: {
  jobWorker: Pick<JobWorker, 'status'>
  response: WorkerResponse
  className?: string
}) {
  const visual = getWorkerResponseVisual(jobWorker, response)
  const Icon = visual.icon
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', visual.chip, className)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {visual.label}
    </span>
  )
})

export const StatusChip = memo(function StatusChip({
  kind,
  label,
  className,
}: {
  kind: JobDisplayKind
  label: string
  className?: string
}) {
  const visual = JOB_STATUS_VISUAL[kind]
  const Icon = visual.icon
  return (
    <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', visual.chip, className)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  )
})

export const WorkerStatusBadge = memo(function WorkerStatusBadge({
  status,
  className,
}: {
  status: WorkerStatus
  className?: string
}) {
  const visual = WORKER_STATUS_VISUAL[status]
  const Icon = visual.icon
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset', visual.chip, className)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {WORKER_STATUS_LABEL[status]}
    </span>
  )
})

export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || '?'
}

export const WorkerAvatar = memo(function WorkerAvatar({
  name,
  avatarUrl,
  status,
  className,
}: {
  name: string
  avatarUrl: string | null
  status?: WorkerStatus
  className?: string
}) {
  return (
    <span className="relative inline-flex shrink-0">
      <Avatar className={cn('h-7 w-7', className)}>
        {avatarUrl ? <AvatarImage src={avatarUrl} alt="" /> : null}
        <AvatarFallback className="bg-[#e8f4ee] text-[10px] font-semibold text-[#20352b]">{getInitials(name)}</AvatarFallback>
      </Avatar>
      {status ? (
        <span
          className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-white', WORKER_STATUS_VISUAL[status].solid)}
          aria-hidden="true"
        />
      ) : null}
    </span>
  )
})
